import { createReducer } from '@reduxjs/toolkit';
import * as AM_COMMON from '@innovyze/lib-am-common/Reducers';
import {
	getDeteriorationModel,
	getDeteriorationModelRejected,
	getDeteriorationModelResolved,
	getDeteriorationModelStatusResolved,
	getIndustryStandardCurvesAction,
	getIndustryStandardCurvesResolved,
	listDeteriorationModelCurveConfigsAction,
	listDeteriorationModelCurveConfigsRejected,
	listDeteriorationModelCurveConfigsResolved,
	listDeteriorationModels,
	listDeteriorationModelsRejected,
	listDeteriorationModelsResolved,
	listDeteriorationModelAssetsResultsAction,
	listDeteriorationModelAssetsResultsResolved,
	listDeteriorationModelAssetsResultsRejected,
	resetDeteriorationModelAssetsResults,
	listDeteriorationModelGroupsResultsAction,
	listDeteriorationModelGroupsResultsResolved,
	listDeteriorationModelGroupsResultsRejected,
	getGlobalUndefinedCountAction,
	getGlobalUndefinedCountResolved,
	getGlobalUndefinedCountRejected,
	setPaginationState,
	clearPaginationState,
	setSelectedBreakdownGrade,
	updatePublishedStatusResolved,
} from '@Actions/deteriorationModels.actions';
import {
	DeteriorationModelsStoreState,
	GetDeteriorationModelCurveConfigsPaginatedRequest,
	GetDeteriorationModelAssetsResultsPaginatedRequests,
	GetDeteriorationModelGroupsResultsPaginatedRequests,
	DeteriorationModelCard,
} from '@Types/deteriorationModels.types';
import { getDefaultAssetsResultsRequest } from '@Utils/assetsResultsRequest';

const getDefaultConfigurationRequest = (modelId: string): GetDeteriorationModelCurveConfigsPaginatedRequest => ({
	modelId,
	page: 1,
	slice: 10,
	sortKey: 'assetGroupName',
	sortDescending: true,
	searchValue: null,
	filter: null,
	filterModel: { items: [] },
});

const getDefaultAssetsRequest = getDefaultAssetsResultsRequest;

const getDefaultGroupsRequest = (
	modelId: string,
	runId: string,
): GetDeteriorationModelGroupsResultsPaginatedRequests => ({
	modelId,
	runId,
	page: 1,
	slice: 10,
	sortKey: 'assetGroupName',
	sortDescending: true,
	searchValue: null,
	filter: null,
	filterModel: { items: [] },
});

export const initState: DeteriorationModelsStoreState = {
	deteriorationModelsList: AM_COMMON.paginationInitialState,
	selectedDeteriorationModel: {
		inProcess: false,
		successful: null,
		deteriorationModel: null,
	},
	deteriorationModelCurveConfigsList: {
		pagination: AM_COMMON.paginationInitialState,
		list: [],
	},
	industryStandardCurves: {
		isLoading: false,
		curves: [],
	},
	globalUndefinedCount: {
		count: 0,
	},
	assetResults: AM_COMMON.paginationInitialState,
	assetResultsRequest: null,
	groupResults: AM_COMMON.paginationInitialState,
	paginationState: {},
	selectedBreakdownGrade: {},
};

export const reducer = createReducer(initState, builder => {
	builder
		.addCase(listDeteriorationModels, state => {
			state.deteriorationModelsList = AM_COMMON.updateIsLoading(AM_COMMON.paginationInitialState, true);
		})
		.addCase(listDeteriorationModelsResolved, (state, action) => {
			state.deteriorationModelsList = AM_COMMON.updateResolvedCard(state.deteriorationModelsList, action.payload);
		})
		.addCase(listDeteriorationModelsRejected, (state, action) => {
			state.deteriorationModelsList = AM_COMMON.updateRejected(state.deteriorationModelsList, action.payload);
		})
		.addCase(getDeteriorationModel, (state, action) => {
			// Refetching the model already on screen (e.g. on returning from asset details)
			// keeps it there while the refresh is in flight. Clearing it dropped the page to
			// its full-page spinner and remounted every tab just to show the same model.
			const loaded = state.selectedDeteriorationModel.deteriorationModel;
			const isRefresh = loaded !== null && loaded.id === action.payload.id;
			state.selectedDeteriorationModel = {
				inProcess: true,
				successful: isRefresh ? state.selectedDeteriorationModel.successful : null,
				deteriorationModel: isRefresh ? loaded : null,
			};
		})
		.addCase(getDeteriorationModelResolved, (state, action) => {
			state.selectedDeteriorationModel = {
				inProcess: false,
				successful: true,
				deteriorationModel: action.payload,
			};
		})
		.addCase(getDeteriorationModelRejected, state => {
			state.selectedDeteriorationModel = {
				inProcess: false,
				successful: false,
				deteriorationModel: null,
			};
		})
		.addCase(getDeteriorationModelStatusResolved, (state, action) => {
			// Only update the status, preserve existing loading states and other model data
			if (
				state.selectedDeteriorationModel.successful !== false &&
				state.selectedDeteriorationModel.deteriorationModel
			) {
				state.selectedDeteriorationModel.deteriorationModel.status = action.payload.status;
			}
		})
		.addCase(listDeteriorationModelCurveConfigsAction, state => {
			state.deteriorationModelCurveConfigsList = {
				pagination: AM_COMMON.updateIsLoading(AM_COMMON.paginationInitialState, true),
				list: [],
			};
		})
		.addCase(listDeteriorationModelCurveConfigsResolved, (state, action) => {
			state.deteriorationModelCurveConfigsList = {
				pagination: AM_COMMON.updateResolved(
					state.deteriorationModelCurveConfigsList.pagination,
					action.payload.response,
				),
				list: action.payload.curveConfigs,
			};
		})
		.addCase(listDeteriorationModelCurveConfigsRejected, (state, action) => {
			state.deteriorationModelCurveConfigsList = {
				pagination: AM_COMMON.updateRejected(state.deteriorationModelCurveConfigsList.pagination, action.payload),
				list: [],
			};
		})
		.addCase(getIndustryStandardCurvesAction, state => {
			state.industryStandardCurves = {
				isLoading: true,
				curves: [],
			};
		})
		.addCase(getIndustryStandardCurvesResolved, (state, action) => {
			state.industryStandardCurves = {
				isLoading: false,
				curves: action.payload,
			};
		})
		.addCase(listDeteriorationModelAssetsResultsAction, (state, action) => {
			state.assetResults = AM_COMMON.updateIsLoading(state.assetResults, true);
			state.assetResultsRequest = action.payload;
		})
		.addCase(listDeteriorationModelAssetsResultsResolved, (state, action) => {
			state.assetResults = AM_COMMON.updateResolved(state.assetResults, action.payload);
		})
		.addCase(listDeteriorationModelAssetsResultsRejected, (state, action) => {
			state.assetResults = AM_COMMON.updateRejected(state.assetResults, action.payload);
			state.assetResultsRequest = null;
		})
		.addCase(resetDeteriorationModelAssetsResults, state => {
			state.assetResults = AM_COMMON.paginationInitialState;
			state.assetResultsRequest = null;
		})
		.addCase(listDeteriorationModelGroupsResultsAction, state => {
			state.groupResults = AM_COMMON.updateIsLoading(state.groupResults, true);
		})
		.addCase(listDeteriorationModelGroupsResultsResolved, (state, action) => {
			state.groupResults = AM_COMMON.updateResolved(state.groupResults, action.payload);
		})
		.addCase(listDeteriorationModelGroupsResultsRejected, (state, action) => {
			state.groupResults = AM_COMMON.updateRejected(state.groupResults, action.payload);
		})
		.addCase(getGlobalUndefinedCountAction, state => {
			state.globalUndefinedCount = {
				count: 0,
			};
		})
		.addCase(getGlobalUndefinedCountResolved, (state, action) => {
			state.globalUndefinedCount = {
				count: action.payload.response.pagination.total,
			};
		})
		.addCase(getGlobalUndefinedCountRejected, state => {
			state.globalUndefinedCount = {
				count: 0,
			};
		})
		.addCase(setPaginationState, (state, action) => {
			const { modelId, tab, paginationRequest } = action.payload;

			if (!state.paginationState[modelId]) {
				state.paginationState[modelId] = {
					configuration: getDefaultConfigurationRequest(modelId),
					results: {
						groups: getDefaultGroupsRequest(modelId, ''),
						assets: getDefaultAssetsRequest(modelId, ''),
					},
				};
			}

			if (tab === 'configuration') {
				state.paginationState[modelId].configuration =
					paginationRequest as GetDeteriorationModelCurveConfigsPaginatedRequest;
			} else if (tab === 'groups') {
				state.paginationState[modelId].results.groups =
					paginationRequest as GetDeteriorationModelGroupsResultsPaginatedRequests;
			} else if (tab === 'assets') {
				state.paginationState[modelId].results.assets =
					paginationRequest as GetDeteriorationModelAssetsResultsPaginatedRequests;
			}
		})
		.addCase(setSelectedBreakdownGrade, (state, action) => {
			const { modelId, grade } = action.payload;
			if (grade) {
				state.selectedBreakdownGrade[modelId] = grade;
			} else {
				delete state.selectedBreakdownGrade[modelId];
			}
		})
		.addCase(clearPaginationState, (state, action) => {
			const { modelId } = action.payload;
			if (state.paginationState[modelId]) {
				delete state.paginationState[modelId].configuration;
			}
		})
		.addCase(updatePublishedStatusResolved, (state, action) => {
			if (state.deteriorationModelsList.cards) {
				const modelIndex = state.deteriorationModelsList.cards.findIndex(
					(model: unknown) => (model as DeteriorationModelCard).id === action.payload.modelId,
				);
				if (modelIndex !== -1) {
					(state.deteriorationModelsList.cards[modelIndex] as DeteriorationModelCard).published =
						action.payload.published;
				}
			}

			if (
				state.selectedDeteriorationModel.deteriorationModel &&
				state.selectedDeteriorationModel.deteriorationModel.id === action.payload.modelId
			) {
				state.selectedDeteriorationModel.deteriorationModel.published = action.payload.published;
			}
		});
});

export default reducer;
